import axios from "axios";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://delivery-bonfimgourmet-244425130782.southamerica-east1.run.app";

// Token storage keys
const TOKEN_KEY = "jwt_token";
const USER_KEY = "user_info";

class AuthService {
  /**
   * Authenticate user and store token
   * @param {string} username
   * @param {string} password
   * @returns {Promise<{access_token: string, token_type: string, role: string}>}
   */
  async login(username, password) {
    try {
      // Note: Login endpoint has double /api (special case)
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email: username, // Backend espera 'email' no payload
        password,
      });

      // Backend pode retornar 'token' ou 'access_token'
      const access_token = response.data.access_token || response.data.token;
      const token_type = response.data.token_type;
      const role = response.data.role;

      if (!access_token) {
        console.error("Erro: Nenhum token retornado pelo servidor");
        throw new Error("No access token received from server");
      }

      // Store token and user info in localStorage
      this.setToken(access_token);
      this.setUserInfo({ username, role: role || "user" });

      return response.data;
    } catch (error) {
      if (error.response) {
        console.error(
          "Erro no login:",
          error.response.status,
          error.response.data
        );
      } else if (error.request) {
        console.error(
          "Erro: Backend não respondeu. Verifique se está rodando em:",
          BACKEND_URL
        );
      } else {
        console.error("Erro ao processar login:", error.message);
      }

      throw error;
    }
  }

  /**
   * Register new user
   * @param {string} username
   * @param {string} password
   * @param {string} role
   * @returns {Promise<{access_token: string, token_type: string, role: string}>}
   */
  async register(username, password, role = "user") {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/register`, {
        username,
        password,
        role,
      });

      const { access_token, token_type, role: userRole } = response.data;

      // Store token and user info
      this.setToken(access_token);
      this.setUserInfo({ username, role: userRole });

      return response.data;
    } catch (error) {
      console.error("Register error:", error);
      throw error;
    }
  }

  /**
   * Get current user info from backend
   * @returns {Promise<{id: number, email: string, roles: string[]}>}
   */
  async getCurrentUser() {
    try {
      const token = this.getToken();
      if (!token) {
        throw new Error("No token found");
      }

      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Backend retorna: {"id":1,"email":"...","roles":["..."]}
      const userData = {
        id: response.data.id,
        email: response.data.email,
        username: response.data.email, // Compatibilidade
        roles: response.data.roles || [],
        role: response.data.roles?.[0] || "user", // Primeiro role como padrão
      };

      // Update stored user info
      this.setUserInfo(userData);

      return userData;
    } catch (error) {
      // Se for 401 (Unauthorized), token é inválido
      if (error.response?.status === 401) {
        console.warn("⚠️ Token inválido ou expirado");
        // Clear invalid token
        this.logout();
      } else {
        console.error(
          "Erro ao validar token:",
          error.response?.status || error.message
        );
        // Clear invalid token
        this.logout();
      }
      throw error;
    }
  }

  /**
   * Logout user and clear stored data
   */
  logout() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }

  /**
   * Get stored JWT token
   * @returns {string|null}
   */
  getToken() {
    return localStorage.getItem(TOKEN_KEY);
  }

  /**
   * Store JWT token
   * @param {string} token
   */
  setToken(token) {
    localStorage.setItem(TOKEN_KEY, token);
  }

  /**
   * Get stored user info
   * @returns {object|null}
   */
  getUserInfo() {
    const userStr = localStorage.getItem(USER_KEY);
    if (!userStr) return null;

    try {
      return JSON.parse(userStr);
    } catch (error) {
      console.error("Error parsing user info:", error);
      return null;
    }
  }

  /**
   * Store user info
   * @param {object} userInfo
   */
  setUserInfo(userInfo) {
    localStorage.setItem(USER_KEY, JSON.stringify(userInfo));
  }

  /**
   * Check if user is authenticated
   * @returns {boolean}
   */
  isAuthenticated() {
    return !!this.getToken();
  }

  /**
   * Check if user has admin role
   * @returns {boolean}
   */
  isAdmin() {
    const userInfo = this.getUserInfo();
    return userInfo?.role === "admin";
  }

  /**
   * Decode JWT token (basic decode without verification)
   * @param {string} token
   * @returns {object|null}
   */
  decodeToken(token) {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  }

  /**
   * Check if token is expired
   * @param {string} token
   * @returns {boolean}
   */
  isTokenExpired(token) {
    const decoded = this.decodeToken(token);
    if (!decoded || !decoded.exp) return true;

    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime;
  }

  /**
   * Check if current token is valid
   * @returns {boolean}
   */
  isTokenValid() {
    const token = this.getToken();
    if (!token) return false;

    return !this.isTokenExpired(token);
  }
}

// Export singleton instance
const authService = new AuthService();
export default authService;
